import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Mail01Icon,
  LockPasswordIcon,
  ViewIcon,
  ViewOffIcon,
  ArrowRight01Icon,
} from '@hugeicons/core-free-icons'
import { useAuth } from '@/contexts/AuthContext'
import type { LoginRequest } from '@/services/api'
import { AuthShell } from '@/components/auth-shell'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { FieldGroup } from '@/components/ui/field'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'

export default function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const [failed, setFailed] = useState(false)
  const { login } = useAuth()
  const form = useForm<LoginRequest>({
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = async (data: LoginRequest) => {
    setFailed(false)
    try {
      const success = await login(data)
      // Start with fresh session/member state so a teacher never uses a stale
      // pre-login role when App chooses the home route.
      if (success) window.location.replace('/')
      else setFailed(true)
    } catch {
      setFailed(true)
    }
  }

  return (
    <AuthShell>
      <header className="login-intro">
        <p className="brand-eyebrow">TU ESPACIO ST. GEORGE</p>
        <h1>Bienvenido de vuelta.</h1>
        <p>
          Inicia sesión para continuar con tu día.
          <br />
          Tu cuenta te llevará al espacio de tu equipo.
        </p>
      </header>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
          noValidate
        >
          {failed && (
            <Alert variant="destructive">
              <AlertDescription>
                No pudimos iniciar sesión. Revisa tu correo, contraseña y
                conexión e inténtalo de nuevo.
              </AlertDescription>
            </Alert>
          )}
          <FieldGroup>
            <FormField
              control={form.control}
              name="email"
              rules={{
                required: 'Ingresa tu correo electrónico',
                pattern: {
                  value: /^\S+@\S+\.\S+$/i,
                  message: 'Ingresa un correo válido',
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Correo electrónico</FormLabel>
                  <InputGroup>
                    <InputGroupAddon align="inline-start">
                      <HugeiconsIcon icon={Mail01Icon} aria-hidden="true" />
                    </InputGroupAddon>
                    <FormControl>
                      <InputGroupInput
                        type="email"
                        autoComplete="username"
                        spellCheck={false}
                        autoCapitalize="none"
                        placeholder="nombre@ejemplo.com"
                        {...field}
                      />
                    </FormControl>
                  </InputGroup>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              rules={{
                required: 'Ingresa tu contraseña',
                minLength: {
                  value: 8,
                  message: 'La contraseña debe tener al menos 8 caracteres',
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <FormLabel>Contraseña</FormLabel>
                    <Link
                      to="/forgot-password"
                      className="text-xs font-medium text-primary hover:underline"
                    >
                      ¿La olvidaste?
                    </Link>
                  </div>
                  <InputGroup>
                    <InputGroupAddon align="inline-start">
                      <HugeiconsIcon
                        icon={LockPasswordIcon}
                        aria-hidden="true"
                      />
                    </InputGroupAddon>
                    <FormControl>
                      <InputGroupInput
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="Tu contraseña"
                        {...field}
                      />
                    </FormControl>
                    <InputGroupAddon align="inline-end">
                      <InputGroupButton
                        type="button"
                        size="icon-sm"
                        aria-label={
                          showPassword
                            ? 'Ocultar contraseña'
                            : 'Mostrar contraseña'
                        }
                        aria-pressed={showPassword}
                        onClick={() => setShowPassword((shown) => !shown)}
                      >
                        <HugeiconsIcon
                          icon={showPassword ? ViewOffIcon : ViewIcon}
                          aria-hidden="true"
                        />
                      </InputGroupButton>
                    </InputGroupAddon>
                  </InputGroup>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldGroup>
          <Button
            type="submit"
            size="lg"
            disabled={form.formState.isSubmitting}
            className="w-full"
          >
            {form.formState.isSubmitting ? (
              <Spinner data-icon="inline-start" />
            ) : null}
            {form.formState.isSubmitting
              ? 'Iniciando sesión…'
              : 'Iniciar sesión'}
            {!form.formState.isSubmitting && (
              <HugeiconsIcon
                icon={ArrowRight01Icon}
                data-icon="inline-end"
                aria-hidden="true"
              />
            )}
          </Button>
        </form>
      </Form>
      <p className="login-help">
        <strong className="font-medium text-foreground">
          ¿Es tu primera vez?
        </strong>
        <br />
        Usa los datos de acceso que te entregó administración. Si todavía no
        tienes una cuenta, solicita tu acceso al equipo de St. George.
      </p>
    </AuthShell>
  )
}
